/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from 'antd/components/notification/index.tsx'.
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import Notification from 'rc-notification';
import classNames from 'classnames';
import createUseNotification from 'antd/es/notification/hooks/useNotification';
import ConfigProvider, { globalConfig } from '../config-provider';
import { cloneElement } from 'antd/es/_util/reactNode';
import { Loading } from '../loading';
import { ConfigProps, NotificationPlacement, NotificationInstance, NotificationApi, getInstance } from 'antd/es/notification/index';
import { Icon } from '../icon';
const notificationInstance = {};
let defaultDuration = 4.5;
let defaultTop = 24;
let defaultBottom = 24;
let defaultPrefixCls = '';
let defaultPlacement = 'topRight';
let defaultGetContainer;
let defaultCloseIcon;
let rtl = false;
let maxCount;
function setNotificationConfig(options) {
    const { duration, placement, bottom, top, getContainer, closeIcon, prefixCls } = options;
    if (prefixCls !== undefined) {
        defaultPrefixCls = prefixCls;
    }
    if (duration !== undefined) {
        defaultDuration = duration;
    }
    if (placement !== undefined) {
        defaultPlacement = placement;
    }
    else if (options.rtl) {
        defaultPlacement = 'topLeft';
    }
    if (bottom !== undefined) {
        defaultBottom = bottom;
    }
    if (top !== undefined) {
        defaultTop = top;
    }
    if (getContainer !== undefined) {
        defaultGetContainer = getContainer;
    }
    if (closeIcon !== undefined) {
        defaultCloseIcon = closeIcon;
    }
    if (options.rtl !== undefined) {
        rtl = options.rtl;
    }
    if (options.maxCount !== undefined) {
        maxCount = options.maxCount;
    }
}
function getPlacementStyle(placement, top = defaultTop, bottom = defaultBottom) {
    let style;
    switch (placement) {
        case 'topLeft':
            style = {
                left: 0,
                top,
                bottom: 'auto'
            };
            break;
        case 'topRight':
            style = {
                right: 0,
                top,
                bottom: 'auto'
            };
            break;
        case 'bottomLeft':
            style = {
                left: 0,
                top: 'auto',
                bottom
            };
            break;
        default:
            style = {
                right: 0,
                top: 'auto',
                bottom
            };
            break;
    }
    return style;
}
function getNotificationInstance(args, callback) {
    const { placement = defaultPlacement, top, bottom, getContainer = defaultGetContainer, closeIcon = defaultCloseIcon, prefixCls: customizePrefixCls } = args;
    const { getPrefixCls, getIconPrefixCls, getAnimation } = globalConfig();
    const prefixCls = getPrefixCls('notification', customizePrefixCls || defaultPrefixCls);
    const iconPrefixCls = getIconPrefixCls();
    const animationConfig = getAnimation?.();
    const cacheKey = `${prefixCls}-${placement}`;
    const cacheInstance = notificationInstance[cacheKey];
    if (cacheInstance) {
        Promise.resolve(cacheInstance).then((instance) => {
            callback({ prefixCls: `${prefixCls}-notice`, iconPrefixCls, instance });
        });
        return;
    }
    const closeIconToRender = (React.createElement("span", { className: `${prefixCls}-close-x` }, closeIcon || React.createElement(Icon, { type: 'close', className: `${prefixCls}-close-icon`, size: 'small' })));
    const notificationClass = classNames(`${prefixCls}-${placement}`, {
        [`${prefixCls}-rtl`]: rtl === true,
        [`${prefixCls}-glassmorphism`]: !!animationConfig.glassmorphism
    });
    notificationInstance[cacheKey] = new Promise((resolve) => {
        Notification.newInstance({
            prefixCls,
            className: notificationClass,
            style: getPlacementStyle(placement, top, bottom),
            getContainer,
            closeIcon: closeIconToRender,
            maxCount
        }, (notification) => {
            resolve(notification);
            callback({
                prefixCls: `${prefixCls}-notice`,
                iconPrefixCls,
                instance: notification
            });
        });
    });
}
const typeToIcon = {
    success: React.createElement(Icon, { type: 'checkmark--filled' }),
    info: React.createElement(Icon, { type: 'information--filled' }),
    error: React.createElement(Icon, { type: 'misuse--filled' }),
    warning: React.createElement(Icon, { type: 'warning--filled' }),
    loading: React.createElement(Loading, null)
};
function getRCNoticeProps(args, prefixCls, iconPrefixCls) {
    const { duration: durationArg, icon, type, description, message, btn, onClose, onClick, key, style, className, withBackground } = args;
    const duration = durationArg === undefined ? defaultDuration : durationArg;
    let iconNode = null;
    if (icon) {
        iconNode = React.createElement("span", { className: `${prefixCls}-icon` }, args.icon);
    }
    else if (type) {
        iconNode = cloneElement(typeToIcon[type], {
            className: `${prefixCls}-icon ${prefixCls}-icon-${type}`
        });
    }
    const autoMarginTag = !description && iconNode ? React.createElement("span", { className: `${prefixCls}-message-single-line-auto-margin` }) : null;
    return {
        content: (React.createElement(ConfigProvider, { iconPrefixCls: iconPrefixCls },
            React.createElement("div", { className: iconNode ? `${prefixCls}-with-icon` : '', role: 'alert' },
                iconNode,
                React.createElement("div", { className: `${prefixCls}-message` },
                    autoMarginTag,
                    message),
                React.createElement("div", { className: `${prefixCls}-description` }, description),
                btn ? React.createElement("span", { className: `${prefixCls}-btn` }, btn) : null))),
        duration,
        closable: true,
        onClose,
        onClick,
        key,
        style: style || {},
        className: classNames(className, {
            [`${prefixCls}-${type}`]: !!type,
            [`${prefixCls}-with-background`]: !!withBackground
        })
    };
}
function notice(args) {
    getNotificationInstance(args, ({ prefixCls, iconPrefixCls, instance }) => {
        instance.notice(getRCNoticeProps(args, prefixCls, iconPrefixCls));
    });
}
const api = {
    open: notice,
    close(key) {
        Object.keys(notificationInstance).forEach((cacheKey) => Promise.resolve(notificationInstance[cacheKey]).then((instance) => {
            instance.removeNotice(key);
        }));
    },
    config: setNotificationConfig,
    destroy() {
        Object.keys(notificationInstance).forEach((cacheKey) => {
            Promise.resolve(notificationInstance[cacheKey]).then((instance) => {
                instance.destroy();
            });
            delete notificationInstance[cacheKey]; // lgtm[js/missing-await]
        });
    }
};
['success', 'info', 'warning', 'error', 'loading'].forEach((type) => {
    api[type] = (args) => api.open({
        ...args,
        type
    });
});
api.warn = api.warning;
api.useNotification = createUseNotification(getNotificationInstance, getRCNoticeProps);
export { getInstance };
export { api as notification };
export default api;
